import {
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
    useImperativeHandle,
    useRef,
    useMemo,
} from 'react';
import { Box } from '@mui/system';

import { FlatPaginationComp } from 'shared/components/Pagination';
import FileSaver from 'file-saver';
import {
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';

import { useNavigate } from 'react-router-dom';
import { fetchMemberList } from './components/data/Main';
import { Toast, Utils } from 'shared/helpers';
import ManageAccessEditDialog from './ManageAccessEditDialog';
import ManageAccessNewDialog from './ManageAccessNewDialog';
import { useDispatch, useSelector } from 'react-redux';
import { ManageAccessService } from 'services';
import { downloadCSV, downloadPDF } from 'shared/helpers/DirectDownload';
import JSZip from 'jszip';
import { AppSecondaryButton } from 'shared/components/AppComponents';
import DownloadModal, { inferDownloadModal } from './DownloadModal';
import { SocketActions } from 'state/socket/Action';
import InfoIcon from '@mui/icons-material/Info';
import { setAccessRowsPerPageNumber } from 'state/persistapplication/Action';
import { useMountedState, useUpdateEffect } from 'react-use';
import { AxiosError } from 'axios';

/**
 * MCMain component.
 * Renders the main content for managing clients.
 * @returns {ReactElement} MCMain component.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MCMain: React.FC<MCMain> = forwardRef((props, ref): ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authState = useSelector((state: any) => state.authState);
    const userInfo = useSelector((state: any) => state.profileState.userInfo);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isShowEditAccess, setShowEditAccess] = useState<boolean>(false);

    const [isDownloading, setDownloading] = useState<boolean>(false);
    const [isShowNewMemberAccess, setShowNewMemberAccess] =
        useState<boolean>(false);
    const [isDownloadProgress, setDownloadProgress] = useState<boolean>(false);
    const downloadModalRef = useRef<inferDownloadModal | null>(null);
    const [contentProgress, setContentProgress] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userData, setData] = useState<any[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [pageCount, setPageCount] = useState(0);
    const [page, setPage] = useState(1);
    const [isMount, setMount] = useState<boolean>(false);
    const [isRefresh, setRefresh] = useState<boolean>(false);
    const [accessState, setAccessState] = useState<any | null>(null);
    const [downloadId, setDownloadId] = useState('');
    const [userId, setUserId] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const persistRowsPerPage = useSelector(
        (state: any) => state.persistState.rowsPerPage.access
    );
    const [rowsPerPage, setRowsPerPage] = useState<number>(persistRowsPerPage);
    const isMounted = useMountedState();

    useLayoutEffect(() => {
        dispatch(setAccessRowsPerPageNumber(rowsPerPage));
    }, [rowsPerPage]);

    useEffect(() => {
        if (isMounted() && persistRowsPerPage !== rowsPerPage) {
            setRowsPerPage(persistRowsPerPage);
        }
    }, []);
    const innerPaginationComp = useRef<HTMLDivElement>(null);
    const socketState = useSelector(
        (stateRedux: any) => stateRedux.socketState
    );

    const socketEvent = useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );

    const csvAuditFile = useMemo(
        () => socketState.socketData.csvSasToken,
        [socketState.socketData.eventName]
    );

    const pdfAuditFile = useMemo(
        () => socketState.socketData.pdfSasToken,
        [socketState.socketData.eventName]
    );

    const socketRequestedUserId = useMemo(
        () => socketState.socketData.requestedUserId,
        [socketState.socketData.eventName]
    );
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(1);
            setLoading(true);
        },
        [setLoading, setPage, setRowsPerPage]
    );
    const [isNetworkError, setNetworkError] = useState<boolean>(false);
    useEffect(() => {
        setMount(true);
        fetchMemberList(page, rowsPerPage)
            .then((response) => {
                const { paginatedResult, totalCount } = response;
                const pageCount = totalCount[0].count;
                setPageCount(pageCount);
                setData(paginatedResult);
                setLoading(false);
            })
            .catch((error) => {
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    setNetworkError(true);
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, []);

    useUpdateEffect(() => {
        if (isMount && (page || isRefresh || rowsPerPage)) {
            fetchMemberList(page, rowsPerPage)
                .then((response) => {
                    const { paginatedResult, totalCount } = response;
                    const pageCount = totalCount[0].count;
                    setPageCount(pageCount);
                    setData(paginatedResult);
                    setLoading(false);
                    if (isRefresh) {
                        setRefresh(false);
                    }
                })
                .catch((error) => {
                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        setNetworkError(true);
                    } else {
                        Utils.handleErrorResponse(error);
                    }
                });
        }
    }, [page, isRefresh, rowsPerPage]);
    useLayoutEffect(() => {
        setNetworkError(false);
    }, [isLoading]);
    /**
     * Handles pagination page change.
     * @param {object} event - The event object.
     * @param {number} newPage - The new page number.
     * @returns {void}
     */
    const onChangePagination = (event: any, newPage: number): void => {
        setLoading(true);
        setPage(newPage);
    };

    const onTableRowCliked = useCallback((row: any) => {
        setShowEditAccess(true);
        setAccessState(row);
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            showNewMemberModal: () => {
                setShowNewMemberAccess(true);
            },
        }),
        []
    );

    /**
     * get all pending PDF using fetch download
     *
     * @param pendingDocs - "string" array of url document to download.
     * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
     */
    const getPendingPDF = useCallback<
        (pendingDocs: string[]) => Promise<Blob>[]
    >(
        (pendingZips: string[]) =>
            Array.from(pendingZips).map(
                (el) =>
                    new Promise(async (resolve, reject) => {
                        downloadPDF(el).then(resolve).catch(reject);
                    })
            ),
        []
    );
    /**
     * get all pending PDF using fetch download
     *
     * @param pendingDocs - "string" array of url document to download.
     * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
     */
    const getPendingCSV = useCallback<
        (pendingDocs: string[]) => Promise<Blob>[]
    >(
        (pendingZips: string[]) =>
            Array.from(pendingZips).map(
                (el) =>
                    new Promise(async (resolve, reject) => {
                        downloadCSV(el).then(resolve).catch(reject);
                    })
            ),
        []
    );

    const handleAuditFilename = useCallback(
        (url: URL, type: string) =>
            url.pathname?.split('/').find((segment) => segment.includes(type)),
        []
    );

    /**
     * Downloads an Zips based on the audited file url.
     *
     * @param arrayOfPendingPDF - "file" url in array.
     * @param arrayOfPendingCSV - "file" url in array.
     * @param packetId - "packetId" in string
     * @returns {Promise<void>} A promise that resolves when all files are downloaded.
     */
    const downloadAuditZips = async (
        pdfAuditFile: string,
        csvAuditFile: string,
        packetId: string,
        email: string
    ): Promise<void> => {
        let promisePendingBlobs: Blob[] = [];

        try {
            const csvAuditFilename = handleAuditFilename(
                new window.URL(csvAuditFile),
                '.csv'
            );
            const pdfAuditFilename = handleAuditFilename(
                new window.URL(pdfAuditFile),
                '.pdf'
            );
            if (pdfAuditFile) {
                const promisePendingDocs = getPendingPDF([pdfAuditFile]);
                const pendingBlobFromDocs = await Promise.all<Blob>(
                    promisePendingDocs
                );
                promisePendingBlobs =
                    promisePendingBlobs.concat(pendingBlobFromDocs);
            }
            if (csvAuditFile) {
                const promisePendingCSV = await getPendingCSV([csvAuditFile]);
                const pendingBlobFromCSV = await Promise.all<Blob>(
                    promisePendingCSV
                );
                promisePendingBlobs =
                    promisePendingBlobs.concat(pendingBlobFromCSV);
            }

            // create new zip when there is processedFile
            const zip = new JSZip();

            promisePendingBlobs.forEach((blob) => {
                if (blob.type === 'application/pdf') {
                    zip.file(
                        pdfAuditFilename || `file-audit-${email}.pdf`,
                        blob
                    );
                } else {
                    zip.file(
                        csvAuditFilename || `file-audit-${email}.csv`,
                        blob
                    );
                }
            });

            zip.generateAsync({ type: 'blob' }).then((content) => {
                // see FileSaver.js
                FileSaver.saveAs(content, `Audit Cross Check For ${packetId}`);
            });
        } catch (error) {
            Utils.handleErrorResponse(error);
        }
    };

    useEffect(() => {
        if (socketEvent === 'userAuditDownload') {
            if (
                isDownloadProgress &&
                // eslint-disable-next-line no-underscore-dangle
                socketRequestedUserId === userInfo._id
            ) {
                downloadAuditZips(
                    pdfAuditFile,
                    csvAuditFile,
                    userId,
                    email
                ).then(() => {
                    setDownloading(false);
                    downloadModalRef.current?.setClose();
                });
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);
    const handlePacketAuditFile = useCallback(
        async (userId: string, email: string) => {
            setDownloading(true);
            setDownloadId(userId);
            ManageAccessService.getMembersAuditFile({
                userId,
            })
                .then((response: any) => {
                    setUserId(userId);
                    setEmail(email);
                    setContentProgress(response.data.msg);
                    setDownloading(false);
                    downloadModalRef.current?.setOpen();
                })
                .catch((error) => {
                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    } else {
                        Utils.handleErrorResponse(error);
                    }
                    setUserId('');
                    setEmail('');
                    setDownloading(false);
                });
        },
        []
    );

    return (
        <Box className="ContainerManageAccess">
            <Box className="WrapperManageAccess">
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>User email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>
                                    Audit
                                    <Tooltip
                                        placement="right-start"
                                        title="Audit logs are available for the last 30 days. For older logs, please contact support."
                                    >
                                        <IconButton>
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                {authState.role === 'clientManager' && (
                                    <TableCell>Edit</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        {isLoading && isNetworkError === false && (
                            <TableBody className="manage-access-relative">
                                <Box className="align-loader-table-manage-access">
                                    <CircularProgress
                                        title="progress loading"
                                        className="mr-2 margin-loader"
                                        size={54}
                                        color="inherit"
                                    />
                                </Box>
                            </TableBody>
                        )}
                        {isLoading && isNetworkError && (
                            <TableBody className="manage-access-relative-empty">
                                <Box className="align-loader-placeholder-manage-access">
                                    <EmptyPlaceholder message="No internet connection, Please try again!" />
                                </Box>
                            </TableBody>
                        )}
                        {!isLoading && userData.length < 1 && (
                            <TableBody className="manage-access-relative-empty">
                                <Box className="align-loader-placeholder-manage-access">
                                    <EmptyPlaceholder message="There are no member records yet" />
                                </Box>
                            </TableBody>
                        )}
                        {!isLoading && userData && userData.length > 0 && (
                            <TableBody>
                                {userData.map((row) => (
                                    <TableRow
                                        key={row.userId}
                                        sx={{
                                            '&:last-child td, &:last-child th':
                                                {
                                                    border: 0,
                                                },
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.email}
                                        </TableCell>
                                        <TableCell>{row.role || '-'}</TableCell>
                                        <TableCell>
                                            {row.enabled
                                                ? 'Enabled'
                                                : 'Disabled'}
                                        </TableCell>
                                        <TableCell>
                                            <AppSecondaryButton
                                                title="Download"
                                                isLoading={
                                                    downloadId === row.userId
                                                        ? isDownloading
                                                        : false
                                                }
                                                disabled={isDownloading}
                                                onClick={() => {
                                                    handlePacketAuditFile(
                                                        row.userId,
                                                        row.email
                                                    );
                                                }}
                                                className={
                                                    row.auditFile === null ||
                                                    row.auditFileCSV === null
                                                        ? 'btn-small cursor-default'
                                                        : 'btn-small cursor-pointer'
                                                }
                                                loadingText="DOWNLOADING"
                                            />
                                        </TableCell>
                                        {authState.role === 'clientManager' && (
                                            <TableCell>
                                                <Button
                                                    disableRipple
                                                    variant="text"
                                                    disabled={
                                                        row.enableEdit === false
                                                    }
                                                    onClick={() =>
                                                        onTableRowCliked(row)
                                                    }
                                                    tabIndex="0"
                                                    className="btn-cursor-focused"
                                                    aria-haspopup="dialog"
                                                >
                                                    Details
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>

                    {Array.isArray(userData) && userData.length > 0 && (
                        <Box className="pagination">
                            <FlatPaginationComp
                                hidden={
                                    (!isLoading && userData.length >= 1) ===
                                    false
                                }
                                className="pagination-manage-access"
                                innerRef={innerPaginationComp}
                                data={pageCount}
                                page={page - 1}
                                count={Math.ceil(pageCount / rowsPerPage)}
                                onPageChange={onChangePagination}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                onChange={onChangePagination}
                                popupPrevNext
                                containerClassName="manageAccess"
                            />
                        </Box>
                    )}
                </TableContainer>
            </Box>
            {isShowEditAccess && (
                <ManageAccessEditDialog
                    showForm={isShowEditAccess}
                    onCloseForm={() => setShowEditAccess(false)}
                    onFinishForm={() => {
                        setShowEditAccess(false);
                        setRefresh(true);
                    }}
                    acccessState={accessState}
                />
            )}
            {isShowNewMemberAccess && (
                <ManageAccessNewDialog
                    showForm={isShowNewMemberAccess}
                    onCloseForm={() => setShowNewMemberAccess(false)}
                    onFinishForm={() => {
                        setShowNewMemberAccess(false);
                        setRefresh(true);
                    }}
                    acccessState={{ test: 'test' }}
                />
            )}
            <DownloadModal
                ref={downloadModalRef}
                content={contentProgress}
                callbackOpenModal={() => {
                    setDownloadProgress(true);
                }}
                callbackClosingModal={() => {
                    setDownloadProgress(false);
                    setContentProgress('');
                    setUserId('');
                    setEmail('');
                }}
            />
        </Box>
    );
});

export default MCMain;
